/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  Box,
  Grid,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { CustomTextInput } from 'components/shared';
import Buttons from 'components/elements/Buttons';
import MuiPhoneNumber from 'material-ui-phone-number';
import { useSelector, useDispatch } from 'react-redux';
import { type RootState } from 'store/rootReducer';
import { type Category } from 'types/pages';
import { type IErrorMessages } from 'pages/Employer/Employees/CreateEmployee';
import { strings } from 'i18n/Strings';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import type { GridValidRowModel } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { updateCustomerService } from 'service';
import { useParams } from 'react-router-dom';
import { emailValidator } from 'utils/validationUtils';
import { pepTypeOptions, maritalStatusOptions } from 'constants/constantText';

interface Props {
  customerInfo?: any;
  getCustomerData: () => Promise<void>;
  setOpenUpdateDetails: Dispatch<SetStateAction<boolean>>;
}

const nonMandatoryFields = ['ppsn'];

const EditCustomerDetailsModel = ({
  customerInfo,
  getCustomerData,
  setOpenUpdateDetails,
}: Props): JSX.Element => {
  const { economicStatusAndOccupation } = useSelector(
    (state: RootState) => state.employeeDetails,
  );

  const dispatch = useDispatch();
  const { id } = useParams();

  const [street, town, postalCode] = customerInfo?.address?.split(',') ?? [];
  const [customerDetails, setCustomerDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    citizenship: '',
    dob: '',
    street: '',
    town: '',
    postalCode: '',
    maritalStatus: '',
    economicStatus: null as number | null,
    economicOccupation: null as number | null,
    ppsn: null as string | null,
    pepType: false,
    isAvcPensionUser: false,
    verifyAddressOnfido: false,
    verifyNameOnfido: false,
  });

  const [customerDetailsError, setCustomerDetailsError] =
    useState<IErrorMessages>({
      firstNameError: '',
      lastNameError: '',
      emailError: '',
      phoneNumberError: '',
      employeeIdError: '',
      employeeContributionError: '',
      employerContributionError: '',
      pepTypeError: undefined,
    });
  const [loader, setLoader] = useState(false);

  const findObjectById = (arr: Category[], id: number): Category | null => {
    if (arr) {
      for (const obj of arr) {
        if (obj.id === id) {
          return obj;
        }
        if (obj.subCategories && obj.subCategories.length > 0) {
          const result = findObjectById(obj.subCategories, id);
          if (result) {
            return result;
          }
        }
      }
      return null;
    }
    return null;
  };

  const handleValidation = (
    type: string,
    e: { target: { value: string } },
  ): void => {
    switch (type) {
      case 'firstName': {
        if (e.target.value === '')
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              firstNameError: strings.displayText.firstNameRequiredText,
            };
          });
        else
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              firstNameError: '',
            };
          });
        return;
      }
      case 'lastName': {
        if (e.target.value === '')
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              lastNameError: strings.displayText.lastNameRequiredText,
            };
          });
        else
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              lastNameError: '',
            };
          });
        return;
      }
      case 'email': {
        if (e.target.value === '')
          setCustomerDetailsError((prev) => {
            return { ...prev, emailError: strings.displayText.emailEmptyText };
          });
        else if (emailValidator(e.target.value))
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              emailError: strings.displayText.invalidEmailText,
            };
          });
        else
          setCustomerDetailsError((prev) => {
            return { ...prev, emailError: '' };
          });

        return;
      }
      case 'pepType': {
        setCustomerDetailsError((prev) => {
          return {
            ...prev,
            pepTypeError:
              e.target.value === undefined
                ? strings.displayText.pepTypeRequiredText
                : undefined,
          };
        });
        return;
      }

      case 'employeeId': {
        if (e.target.value === '')
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              employeeIdError: strings.displayText.employeeIdRequiredText,
            };
          });
        else
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              employeeIdError: '',
            };
          });
        return;
      }
      case 'employeeContribution': {
        if (e.target.value === '')
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              employeeContributionError:
                strings.displayText.employeeContribution,
            };
          });
        else
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              employeeContributionError: '',
            };
          });
        return;
      }
      case 'employerContribution': {
        if (e.target.value === '')
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              employerContributionError:
                strings.displayText.employerContribution,
            };
          });
        else
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              employerContributionError: '',
            };
          });
        return;
      }
      case 'employeeContributionPercentage': {
        if (e.target.value === '')
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              employeeContributionPercentageError:
                strings.displayText.employeeContributionPercentage,
            };
          });
        else
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              employeeContributionPercentageError: '',
            };
          });
        return;
      }
      case 'employerContributionPercentage': {
        if (e.target.value === '')
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              employerContributionPercentageError:
                strings.displayText.employerContributionPercentage,
            };
          });
        else
          setCustomerDetailsError((prev) => {
            return {
              ...prev,
              employerContributionPercentageError: '',
            };
          });
      }
    }
  };

  const handlePhoneValidation = (value: string): void => {
    const phoneNumber = parsePhoneNumberFromString(value);
    const isValid = phoneNumber?.isValid();

    if (isValid) {
      setCustomerDetailsError((prev) => {
        return {
          ...prev,
          phoneNumberError: '',
        };
      });
    } else {
      setCustomerDetailsError((prev) => {
        return {
          ...prev,
          phoneNumberError: strings.displayText.phoneNumberRequiredText,
        };
      });
    }
  };

  const getErrorFieldName = (fieldName: string): string => {
    if (fieldName === 'firstName') {
      return strings.displayText.firstName;
    } else if (fieldName === 'lastName') {
      return strings.displayText.lastName;
    } else {
      return strings.displayText.mandatoryFields;
    }
  };

  /**
   * Validates form data during submission by checking required fields
   * @param submitData - The form data to validate
   * @returns True if validation passes, false otherwise
   */
  const handleValidationDuringSubmit = (
    submitData?: GridValidRowModel,
  ): boolean => {
    if (submitData) {
      const keys: string[] = Object.keys(submitData);
      let validate = true;
      for (let fieldIndex = 0; fieldIndex < keys.length; fieldIndex++) {
        const fieldName = keys[fieldIndex].split(/(?=[A-Z])/);
        const fieldNameStr: string = fieldName.join(' ');

        if (nonMandatoryFields.includes(fieldNameStr)) {
          continue;
        }

        if (fieldNameStr === 'economic Status') {
          if (
            customerDetails.economicOccupation === null &&
            customerDetails.economicStatus === null
          ) {
            dispatch(
              setSnackBarFailed({
                snackBarMessage: strings.displayText.fillEconomicStatus,
              }),
            );
            validate = false;
            break;
          }
          if (
            customerDetails.economicStatus !== 12 &&
            customerDetails.economicOccupation === null
          ) {
            dispatch(
              setSnackBarFailed({
                snackBarMessage: strings.displayText.fillEconomicOccupation,
              }),
            );
            validate = false;
            break;
          }
        }

        if (fieldNameStr === 'phone' && customerDetails.phone === '') {
          dispatch(
            setSnackBarFailed({
              snackBarMessage: strings.displayText.fillPhoneNumber,
            }),
          );
          validate = false;
          break;
        }

        if (fieldNameStr === 'dob' && customerDetails.dob === '') {
          dispatch(
            setSnackBarFailed({
              snackBarMessage: strings.displayText.fillDob,
            }),
          );
          validate = false;
          break;
        }

        if (submitData && submitData[keys[fieldIndex]] === '') {
          const fieldNameError = getErrorFieldName(fieldNameStr);
          dispatch(
            setSnackBarFailed({
              snackBarMessage: `Kindly fill the ${fieldNameError}`,
            }),
          );
          validate = false;
          break;
        }
      }
      return validate;
    }
    return true;
  };

  const handleSaveCustomerDetails = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    try {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);

      // Address from street, town and postalcode
      const street = formData.get('street')?.toString() ?? '';
      const town = formData.get('town')?.toString() ?? '';
      const postalCode = formData.get('postal')?.toString() ?? '';

      const address = `${street}, ${town}, ${postalCode}`;

      const data = {
        firstName: formData.get('firstName')?.toString() ?? '',
        lastName: formData.get('lastName')?.toString() ?? '',
        email: formData.get('email')?.toString() ?? '',
        phone: formData.get('phoneNumber')?.toString(),
        economicStatus:
          economicStatusAndOccupation?.categories?.find(
            (category) => category.id === customerDetails.economicStatus,
          )?.subCategories?.length > 0
            ? customerDetails.economicOccupation
            : customerDetails.economicStatus,
        dob:
          customerDetails?.dob === null
            ? null
            : dayjs(customerDetails?.dob).format('YYYY-MM-DD'),
        country: formData.get('country')?.toString() ?? '',
        citizenship: formData.get('citizenship')?.toString() ?? '',
        maritalStatus: customerDetails?.maritalStatus ?? '',
        pepType: customerDetails?.pepType,
        isAvcPensionUser: customerDetails?.isAvcPensionUser,
        street,
        town,
        postalCode,
        address,
        ppsn: customerDetails?.ppsn ?? '',
        verifyAddressOnfido: customerDetails.verifyAddressOnfido,
        verifyNameOnfido: customerDetails.verifyNameOnfido,
      };
      const submitCheck = handleValidationDuringSubmit(data);
      if (!submitCheck) {
        return;
      }
      setLoader(true);
      await updateCustomerService(id, data.email, data.phone, data);

      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.detailsUpdatedSuccussfully,
        }),
      );
      setOpenUpdateDetails(false);
      setLoader(false);
      void getCustomerData();
    } catch (error) {
      console.log('error', error);
      setLoader(false);
    }
  };

  useEffect(() => {
    const occupationCategory = findObjectById(
      economicStatusAndOccupation?.categories,
      customerInfo.economicStatus,
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setCustomerDetails(() => ({
      firstName: customerInfo.firstName,
      lastName: customerInfo.lastName,
      email: customerInfo.email,
      phone: customerInfo.phone,
      country: customerInfo.country,
      citizenship: customerInfo.citizenship,
      dob: customerInfo?.dob,
      street: street?.trim() ?? '',
      town: town?.trim() ?? '',
      postalCode: postalCode?.trim() ?? '',
      maritalStatus: customerInfo?.maritalStatus ?? '',
      economicStatus: occupationCategory?.parentId ?? null,
      economicOccupation: occupationCategory?.id ?? null,
      ppsn: customerInfo.ppsnNumber,
      pepType: customerInfo?.pepType ?? false,
      isAvcPensionUser: customerInfo?.isAvcPensionUser ?? false,
    }));
  }, []);

  const isDisabled =
    customerDetailsError.emailError !== '' ||
    customerDetailsError.firstNameError !== '' ||
    customerDetailsError.lastNameError !== '' ||
    customerDetailsError.phoneNumberError !== '' ||
    customerDetailsError.employeeIdError !== '' ||
    loader;

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={handleSaveCustomerDetails}
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <CustomTextInput
            value={customerDetails?.firstName ?? ''}
            autoFocus={true}
            isFirstChild={true}
            required={true}
            label={'First Name'}
            name={'firstName'}
            onChange={(e) => {
              setCustomerDetails((prev) => ({
                ...prev,
                firstName: e.target.value,
              }));
              handleValidation('firstName', e);
            }}
            helperText={customerDetailsError?.firstNameError}
          />
        </Grid>
        <Grid item xs={6}>
          <CustomTextInput
            value={customerDetails?.lastName ?? ''}
            required={true}
            label={'Last Name'}
            name={'lastName'}
            onChange={(e) => {
              setCustomerDetails((prev) => ({
                ...prev,
                lastName: e.target.value,
              }));
              handleValidation('lastName', e);
            }}
            helperText={customerDetailsError?.lastNameError}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <CustomTextInput
            value={customerDetails?.email ?? ''}
            required={true}
            label={'Email Address'}
            name={'email'}
            onChange={(e) => {
              setCustomerDetails((prev) => ({
                ...prev,
                email: e.target.value,
              }));
              handleValidation('email', e);
            }}
            helperText={customerDetailsError?.emailError}
          />
        </Grid>
        <Grid item xs={6}>
          <MuiPhoneNumber
            countryCodeEditable={false}
            required={true}
            label={'Phone Number'}
            name={'phoneNumber'}
            defaultCountry={'ie'}
            value={customerDetails.phone}
            onChange={(value: string) => {
              setCustomerDetails((prev) => ({
                ...prev,
                phone: value,
              }));
              handlePhoneValidation(value);
            }}
            variant="outlined"
            fullWidth
            helperText={customerDetailsError?.phoneNumberError}
            sx={{
              '& p': {
                color: 'red',
              },
              my: 2,
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <CustomTextInput
              value={customerDetails.country}
              label={'Country'}
              required={true}
              name={'country'}
              onChange={(e) => {
                setCustomerDetails((prev) => ({
                  ...prev,
                  country: e.target.value,
                }));
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <CustomTextInput
              value={customerDetails.citizenship}
              label={'Residency'}
              required={true}
              name={'citizenship'}
              onChange={(e) => {
                setCustomerDetails((prev) => ({
                  ...prev,
                  citizenship: e.target.value,
                }));
              }}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <CustomTextInput
            value={customerDetails?.dob ?? ''}
            type="date"
            autoFocus={true}
            isFirstChild={true}
            required={true}
            label={'Date of Birth'}
            name={'dob'}
            onChange={(e) => {
              setCustomerDetails((prev) => ({
                ...prev,
                dob: e.target.value,
              }));
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="maritalStatus">
              {strings.displayText.maritalStatus}
            </InputLabel>
            <Select
              labelId="maritalStatus"
              label={strings.displayText.maritalStatus}
              name={'maritalStatus'}
              value={customerDetails.maritalStatus}
              onChange={(e) => {
                setCustomerDetails((prev) => ({
                  ...prev,
                  maritalStatus: e.target.value,
                }));
              }}
            >
              {maritalStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <CustomTextInput
            value={customerDetails.street ?? ''}
            label={'Street'}
            required={true}
            name={'street'}
            onChange={(e) => {
              setCustomerDetails((prev) => ({
                ...prev,
                street: e.target.value,
              }));
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <CustomTextInput
            value={customerDetails.town ?? ''}
            autoFocus={true}
            required={true}
            isFirstChild={true}
            label={'Town'}
            name={'town'}
            onChange={(e) => {
              setCustomerDetails((prev) => ({
                ...prev,
                town: e.target.value,
              }));
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <CustomTextInput
            value={customerDetails.postalCode ?? ''}
            label={'Postal Code'}
            required={true}
            name={'postal'}
            onChange={(e) => {
              setCustomerDetails((prev) => ({
                ...prev,
                postalCode: e.target.value,
              }));
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={25}>
        <Grid item xs={5}>
          <FormControl key={customerDetails.economicStatus} fullWidth>
            <InputLabel id="economicStatus">Economic Status</InputLabel>
            <Select
              labelId="economicStatus"
              value={customerDetails.economicStatus}
              onChange={(e: any) => {
                setCustomerDetails((prev) => ({
                  ...prev,
                  economicStatus: e.target.value,
                  economicOccupation: null,
                }));
              }}
              label="Economic status"
              style={{ width: '425px' }}
              name="economicStatus"
            >
              {economicStatusAndOccupation?.categories?.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.categoryName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {customerDetails.economicStatus &&
          economicStatusAndOccupation?.categories?.find(
            (category) => category.id === customerDetails.economicStatus,
          )?.subCategories?.length > 0 && (
            <Grid item xs={5}>
              <FormControl fullWidth>
                <InputLabel id="economicOccupation">
                  Economic Occupation
                </InputLabel>
                <Select
                  labelId="economicOccupation"
                  value={customerDetails.economicOccupation}
                  onChange={(e) => {
                    setCustomerDetails((prev) => ({
                      ...prev,
                      economicOccupation: e.target.value,
                    }));
                  }}
                  label="Economic occupation"
                  style={{ width: '425px' }}
                  name="economicOccupation"
                >
                  {economicStatusAndOccupation?.categories
                    .find(
                      (category) =>
                        category.id === customerDetails.economicStatus,
                    )
                    ?.subCategories?.map((subcategory) => (
                      <MenuItem key={subcategory.id} value={subcategory.id}>
                        {subcategory.categoryName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Grid item xs={5}>
                {' '}
              </Grid>
              <Grid item xs={12} style={{ marginTop: '16px' }}></Grid>
            </Grid>
          )}
      </Grid>
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Grid item xs={6}>
          <CustomTextInput
            value={customerDetails.ppsn ?? ''}
            autoFocus={true}
            isFirstChild={true}
            label={'PPSN'}
            name={'ppsn'}
            inputProps={{ maxLength: 9 }}
            onChange={(e) => {
              setCustomerDetails((prev) => ({
                ...prev,
                ppsn: e.target.value.toUpperCase(),
              }));
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="pepType">
              {strings.displayText.pepTypeText}
            </InputLabel>
            <Select
              label={strings.displayText.pepTypeText}
              name={'pepType'}
              required={true}
              value={customerDetails.pepType ?? ''}
              onChange={(e) => {
                setCustomerDetails((prev) => ({
                  ...prev,
                  pepType: e.target.value,
                }));
              }}
            >
              {pepTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="isAvcPensionUser">AVC Details</InputLabel>
            <Select
              labelId="isAvcPensionUser"
              label="AVC Details"
              name={'isAvcPensionUser'}
              value={String(customerDetails.isAvcPensionUser)}
              onChange={(e) => {
                setCustomerDetails((prev) => ({
                  ...prev,
                  isAvcPensionUser: e.target.value === 'true',
                }));
              }}
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            label={strings.displayText.verifyNameInOnfido}
            sx={{ mt: 0.5, ml: 0, mr: 0 }}
            control={
              <Checkbox
                name="verifyNameOnfido"
                checked={customerDetails.verifyNameOnfido}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setCustomerDetails({
                    ...customerDetails,
                    [event.target.name]: event.target.checked,
                  });
                }}
              />
            }
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            label={strings.displayText.verifyAddressInOnfido}
            sx={{ mt: 0.5, ml: 0, mr: 0 }}
            control={
              <Checkbox
                name="verifyAddressOnfido"
                checked={customerDetails.verifyAddressOnfido}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setCustomerDetails({
                    ...customerDetails,
                    [event.target.name]: event.target.checked,
                  });
                }}
              />
            }
          />
        </Grid>
      </Grid>
      <Buttons
        disabled={isDisabled}
        style={{ py: '0.8rem', mt: '1rem' }}
        type="submit"
        fullWidth={true}
        endIcon={loader ? <CircularProgress size={15} /> : null}
      >
        {'Save'}
      </Buttons>
    </Box>
  );
};

export default EditCustomerDetailsModel;
